import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useParams } from 'react-router-dom';
import { pollTeacherMessage, openTeacherMessageEvents, acknowledgeTeacherMessage } from './miniTasksApi';
import { readStoredBoardSession } from './sessionStorage';
import { SSE_HTTP_FALLBACK_INTERVAL_MS } from '../live/sseHttpFallbackPolicy';

export default function SmartboardTeacherMessage({ children }) {
  const { token } = useParams();
  const [connected, setConnected] = useState(false);
  const [message, setMessage] = useState(null);
  const [typed, setTyped] = useState('');
  const messageId = message?.id;
  const messageText = message?.text;
  const hasMessage = Boolean(message);

  useEffect(() => {
    const announceLive = (connected) => window.dispatchEvent(new CustomEvent('dandu:board-live:status', { detail: { connected } }));
    let active = true;
    let pending = false;
    let timer = null;
    let waitForBoard = null;
    let source = null;
    let sseRecoveryTimer = null;
    let sseRetryTimer = null;

    const poll = async () => {
      if (pending || !active || !token) return;
      pending = true;
      try {
        const stored = await readStoredBoardSession(token);
        if (!stored?.board_session_id) {
          if (active) setConnected(false);
          return;
        }
        const result = await pollTeacherMessage(token, stored.board_session_id);
        if (active) {
          setConnected(true);
          setMessage(result.message || null);
        }
      } catch (err) {
        if (active) {
          setConnected(false);
          if ([403, 404, 410].includes(err?.status)) setMessage(null);
        }
      } finally {
        pending = false;
      }
    };

    const fallbackToPolling = () => {
      if (!active || timer !== null) return;
      if (sseRetryTimer !== null) {
        window.clearTimeout(sseRetryTimer);
        sseRetryTimer = null;
      }
      if (sseRecoveryTimer !== null) {
        window.clearTimeout(sseRecoveryTimer);
        sseRecoveryTimer = null;
      }
      if (source) {
        source.close();
        source = null;
      }
      if (waitForBoard) {
        window.clearInterval(waitForBoard);
        waitForBoard = null;
      }
      setConnected(false);
      announceLive(false);
      poll();
      timer = window.setInterval(poll, SSE_HTTP_FALLBACK_INTERVAL_MS);
    };

    const canUseSse = typeof EventSource !== 'undefined';

    if (!canUseSse) {
      fallbackToPolling();
    } else {
      let connecting = false;
      const connectWhenBoardReady = async () => {
        if (!active || timer !== null || source || connecting) return;
        connecting = true;
        try {
          const stored = await readStoredBoardSession(token);
          if (!active || timer !== null || !stored?.board_session_id) return;
          if (waitForBoard) {
            window.clearInterval(waitForBoard);
            waitForBoard = null;
          }
          const eventSource = openTeacherMessageEvents(token, stored.board_session_id);
          source = eventSource;
          eventSource.addEventListener('snapshot', (event) => {
            if (!active || source !== eventSource) return;
            let payload;
            try {
              payload = JSON.parse(event.data);
            } catch (_) {
              fallbackToPolling();
              return;
            }
            if (sseRecoveryTimer !== null) {
              window.clearTimeout(sseRecoveryTimer);
              sseRecoveryTimer = null;
            }
            setMessage(payload.message || null);
            setConnected(true);
            announceLive(true);
            if (payload.message?.id) {
              // ACK failures must not invalidate an otherwise healthy stream.
              Promise.resolve()
                .then(() => acknowledgeTeacherMessage(token, stored.board_session_id, payload.message.id))
                .catch(() => { /* Reconnect can retry conditional ACK. */ });
            }
          });
          eventSource.addEventListener('book_reward', (event) => {
            if (!active || source !== eventSource) return;
            try {
              const reward = JSON.parse(event.data);
              window.dispatchEvent(new CustomEvent('dandu:board-live:reward', { detail: reward }));
            } catch (_) { /* Invalid optional reward is ignored. */ }
          });
          eventSource.onerror = () => {
            if (!active || source !== eventSource) return;
            setConnected(false);
            announceLive(false);
            // EventSource has native reconnect. Give transient network loss
            // time to recover and re-read the canonical snapshot; on a sustained
            // error (including disabled backend) revert to safe HTTP polling.
            if (sseRecoveryTimer === null) {
              sseRecoveryTimer = window.setTimeout(fallbackToPolling, 12000);
            }
            // HTTP 503 closes Chromium's EventSource permanently. Retry that
            // terminal state within the same grace window; CONNECTING sources
            // retain their native reconnect without a competing connection.
            if (eventSource.readyState === 2 && sseRetryTimer === null) {
              eventSource.close();
              source = null;
              sseRetryTimer = window.setTimeout(() => {
                sseRetryTimer = null;
                connectWhenBoardReady();
              }, 3000);
            }
          };
        } catch (_) {
          fallbackToPolling();
        } finally {
          connecting = false;
        }
      };
      connectWhenBoardReady();
      // A fresh board link has no local board-session identity until initial
      // GET /board finishes. This checks localStorage only, not the server.
      waitForBoard = window.setInterval(connectWhenBoardReady, 1000);
    }

    return () => {
      active = false;
      announceLive(false);
      if (sseRecoveryTimer !== null) window.clearTimeout(sseRecoveryTimer);
      if (sseRetryTimer !== null) window.clearTimeout(sseRetryTimer);
      if (source) source.close();
      if (waitForBoard) window.clearInterval(waitForBoard);
      if (timer !== null) window.clearInterval(timer);
    };
  }, [token]);

  useEffect(() => {
    if (!hasMessage) {
      setTyped('');
      return undefined;
    }
    setTyped('');
    let index = 0;
    const timer = window.setInterval(() => {
      index += 1;
      setTyped(messageText.slice(0, index));
      if (index >= messageText.length) window.clearInterval(timer);
    }, 60);
    return () => window.clearInterval(timer);
  }, [hasMessage, messageId, messageText]);

  return (
    <>
      {children}
      <Chip
        size="small"
        label={connected ? 'Tahta bağlı' : 'Bağlantı yok'}
        color={connected ? 'success' : 'default'}
        sx={{ position: 'fixed', right: 12, bottom: 12, zIndex: 1500, fontWeight: 800 }}
      />
      {message && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1450, bgcolor: 'rgba(24,16,70,0.72)', display: 'grid', placeItems: 'center', p: 3 }}>
          <Box sx={{ width: 'min(920px, 100%)', borderRadius: 4, bgcolor: '#fffaf0', border: '5px solid #7c3aed', boxShadow: '0 24px 70px rgba(0,0,0,0.3)', p: { xs: 3, md: 5 }, textAlign: 'center' }}>
            <Typography sx={{ color: '#5b21b6', fontWeight: 900, mb: 2, fontSize: { xs: 20, md: 28 } }}>
              Öğretmeninden mesaj
            </Typography>
            <Typography sx={{ color: '#242061', fontWeight: 900, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: { xs: 30, md: 48 }, lineHeight: 1.3 }}>
              {typed}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
}
